/**
 * Public listing visibility rules (Milestone 2).
 */
import {
  AVAILABILITY_STATUS,
  LISTING_STATUS,
  PUBLIC_STATUS,
} from "../../constants/automapListingConstants";

const HIDDEN_STATUSES = new Set([
  LISTING_STATUS.ARCHIVED,
  LISTING_STATUS.REJECTED,
  LISTING_STATUS.DRAFT,
]);

const SEARCH_HIDDEN_STATUSES = new Set([
  ...HIDDEN_STATUSES,
  LISTING_STATUS.SOLD,
  LISTING_STATUS.PAUSED,
  LISTING_STATUS.PENDING_REVIEW,
]);

export function getListingPublicStatus(listing) {
  if (!listing?.id) return PUBLIC_STATUS.NOT_FOUND;

  const status = listing.status || LISTING_STATUS.UNKNOWN;
  const availability = listing.availabilityStatus || AVAILABILITY_STATUS.UNKNOWN;

  if (HIDDEN_STATUSES.has(status)) return PUBLIC_STATUS.HIDDEN;
  if (status === LISTING_STATUS.SOLD || availability === AVAILABILITY_STATUS.SOLD) {
    return PUBLIC_STATUS.UNAVAILABLE;
  }
  if (status === LISTING_STATUS.PAUSED) return PUBLIC_STATUS.HIDDEN;
  if (availability === AVAILABILITY_STATUS.UNAVAILABLE) return PUBLIC_STATUS.UNAVAILABLE;

  if (status === LISTING_STATUS.PENDING_REVIEW) return PUBLIC_STATUS.HIDDEN;

  if (status === LISTING_STATUS.ACTIVE) {
    if (
      availability === AVAILABILITY_STATUS.AVAILABLE ||
      availability === AVAILABILITY_STATUS.PENDING_CONFIRMATION
    ) {
      return PUBLIC_STATUS.VISIBLE;
    }
  }

  // M18: unknown/malformed listings stay hidden until explicitly active + available.
  return PUBLIC_STATUS.HIDDEN;
}

export function isListingPubliclyVisible(listing) {
  return getListingPublicStatus(listing) === PUBLIC_STATUS.VISIBLE;
}

export function shouldShowListingInSearch(listing) {
  if (!listing?.id) return false;
  const status = listing.status || LISTING_STATUS.UNKNOWN;
  if (SEARCH_HIDDEN_STATUSES.has(status)) return false;
  if (listing.availabilityStatus === AVAILABILITY_STATUS.SOLD) return false;
  if (listing.availabilityStatus === AVAILABILITY_STATUS.UNAVAILABLE) return false;
  return getListingPublicStatus(listing) === PUBLIC_STATUS.VISIBLE;
}

export function shouldShowUnavailableDetailPage(listing) {
  if (!listing?.id) return false;
  const publicStatus = getListingPublicStatus(listing);
  return publicStatus === PUBLIC_STATUS.UNAVAILABLE;
}

/** @returns {import('../../constants/automapListingConstants').PUBLIC_STATUS[keyof import('../../constants/automapListingConstants').PUBLIC_STATUS]} */
export function getVehicleDetailPublicStatus(listing) {
  return getListingPublicStatus(listing);
}

/**
 * Whether a listing may render a public detail page (visible or sold/unavailable state).
 */
export function shouldShowVehicleDetail(listing) {
  if (!listing?.id) return false;
  const publicStatus = getListingPublicStatus(listing);
  return publicStatus === PUBLIC_STATUS.VISIBLE || publicStatus === PUBLIC_STATUS.UNAVAILABLE;
}

export function listingHasDealerGap(listing) {
  return !listing?.dealerId && !listing?.dealerName;
}

export function listingHasCriticalFieldGaps(listing) {
  return {
    missingPrice: listing?.priceCop == null,
    missingMileage: listing?.mileageKm == null,
    missingImages: !listing?.primaryImageUrl,
    missingDealer: listingHasDealerGap(listing),
  };
}
